import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import SurveyEntryPage from "./Pages/SurveyEntryPage";
import Login from "./User/Login";
import ForgetPWd from "./User/ForgetPWd";
import DirectoryPage from "./Pages/DirectoryPage";
import TenantManagementPage from "./Pages/TenantManagementPage";
import TenantFormPage from "./Pages/TenantFormPage";
import DigitalReachPage from "./Pages/DigitalReachPage";
import ImportDataPage from "./Pages/ImportDataPage";
import { Toaster } from "react-hot-toast";
import AccountSetting from "./Pages/AccountSetting";
import ThemeSettings from "./Pages/ThemeSettings";
import ProfileSetting from "./Pages/ProfileSetting";
import DocumentationPage from "./Pages/DocumentationPage";
import SelectTenantPage from "./Pages/SelectTenantPage";
import CrossLoginBridge from "./Pages/CrossLoginBridge";
import ResetPassword from "./User/ResetPassword";
import HubsportForm from "./Pages/HubsportForm";
import ScheduleMeet from "./Pages/ScheduleMeet";
import Sidebar from "./Components/Sidebar";
import Usersbar from "./Components/Usersbar";
import { useEffect, useState } from "react";
import VoterForm from "./Pages/VoterForm";
import GeneratePassword from "./User/GeneratePassword";
import WalkingListPage from "./Pages/WalkingListPage";
import DatabasePage from "./Pages/DatabasePage";
import CanvassMapPage from "./Pages/CanvassMapPage";
import { RiMenu3Fill } from "react-icons/ri";
import logo from "./images/Header-Logo.svg";
import { SiGooglemeet } from "react-icons/si";
import { IoNewspaperOutline } from "react-icons/io5";
import { HiUsers } from "react-icons/hi2";
import { MdGpsFixed, MdOutlineImportExport } from "react-icons/md";
import { BsDatabase } from "react-icons/bs";
import { AiOutlineHome } from "react-icons/ai";
import { RxCross2 } from "react-icons/rx";
import ReactDOM from "react-dom";
import LockButton from "./Components/LockButton";
import useFirebaseMessaging from "./Components/firebase-hook";
import { messagingReady } from "./config/firebase";
import GhostModeBanner from "./Components/GhostModeBanner";
import UpdateAvailableBanner from "./Components/UpdateAvailableBanner";
import VersionLabel from "./Components/VersionLabel";
import useAppUpdate from "./Hooks/useAppUpdate";
import NotificationPrompt from "./Components/NotificationPrompt";
import HouseholdsPage from "./Pages/Households";
import { GhostModeProvider, useGhostMode } from "./context/GhostModeContext";
import DiscardOfflineChangesPrompt from "./Components/DiscardOfflineChangesPrompt";
import { ThemeProvider } from "./context/ThemeContext";
import { FaHouseUser } from "react-icons/fa";
import { ROLES, getStoredRole, getStoredIsSuperAdmin } from "./constants/roles";
import { needsTenantSelection } from "./utils/session";

const CheckLoginRoute = ({ redirectPath = "/dashboard" }) => {
  const auth = localStorage.getItem("token");
  if (auth) {
    return <Navigate to={redirectPath} replace />;
  }
  return <Outlet />;
};

// allowedRoles is an allow-list mode (only these may pass), disallowedRoles is
// the original deny-list mode (everyone except these may pass) — mutually
// exclusive, only one should be passed per usage. "ROLE_SUPER_ADMIN" is a
// special-cased allowedRoles value checked against isSuperAdmin (a separate
// localStorage flag, not part of userRole's HubSpot-role string set — see
// Task F5.1's isAppAdmin/isSuperAdmin addition to the signin/select-tenant/switch-tenant
// responses), since the global super_admin role has no representation in
// userRole otherwise.
const RoleProtectedRoute = ({ disallowedRoles, allowedRoles, redirectPath = "/dashboard" }) => {
  const userRole = getStoredRole();
  const isSuperAdmin = getStoredIsSuperAdmin();

  if (allowedRoles) {
    const allowed = allowedRoles.some((role) =>
      role === ROLES.SUPER_ADMIN ? isSuperAdmin : role === userRole
    );
    if (!allowed) {
      return <Navigate to={redirectPath} replace />;
    }
    return <Outlet />;
  }

  if (disallowedRoles.includes(userRole)) {
    return <Navigate to={redirectPath} replace />;
  }
  return <Outlet />;
};

const ProtectedRoute = ({ redirectPath = "/" }) => {
  const auth = localStorage.getItem("token");
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const { currentVersion } = useAppUpdate();
  const [showSideBar, setShowSideBar] = useState(
    !(localStorage.getItem("lock") === "true") ? true : false
  );

  const [hideSidebarChecked, setHideSidebarChecked] = useState(
    localStorage.getItem("lock") === "true" ? true : false
  );
  const { isGhostMode, ghostedUser, exitGhostMode, isTransitioning, transitionMessage } = useGhostMode();

  const toggleSidebar = (hide) => {
    setShowSideBar(!hide);
  };

  if (!auth) {
    return <Navigate to={redirectPath} replace />;
  }

  if (needsTenantSelection()) {
    return <Navigate to="/select-tenant" replace />;
  }

  const userRole = getStoredRole();
  const isSuperAdmin = getStoredIsSuperAdmin();

  const SidebarData = [
    {
      title: "",
      data: [
        {
          text: "Dashboard",
          path: "/dashboard",
          icon: <AiOutlineHome />,
        },
      ],
    },
    {
      title: "Data",
      data: [
        ...(userRole === ROLES.REGIONAL_MANAGER ? [] : [
          {
            text: "  Import Data",
            path: "/import-data",
            icon: <MdOutlineImportExport />,
          },
        ]),
        {
          text: " Walking list",
          path: "/walking-list",
          icon: <BsDatabase />,
        },

        ...(userRole === ROLES.CANVASSER ? [] : [
          {
            text: "Database",
            path: "/database",
            icon: <BsDatabase />,
          },
          {
            text: "Households",
            path: "/households",
            icon: <FaHouseUser />,
          },
        ]),
        {
          text: " Canvass Map",
          path: "/canvass-map",
          icon: <MdGpsFixed />,
        },
      ].filter(Boolean),
    },
    {
      title: "Settings",
      data: [
        ...(userRole === ROLES.REGIONAL_MANAGER || userRole === ROLES.CANVASSER ? [] : [
          {
            text: " Directory",
            path: "/directory",
            icon: <HiUsers />,
          },
        ]),
        {
          text: "Surveys",
          path: "/surveyentries",
          icon: <IoNewspaperOutline />,
        },
        {
          text: "Meet Us",
          path: "/schedule-meeting",
          icon: <SiGooglemeet />,
        },
      ],
    },
    ...(isSuperAdmin ? [
      {
        title: "Administration",
        data: [
          {
            text: "Tenant Management",
            path: "/tenant-management",
            icon: <HiUsers />,
          },
        ],
      },
    ] : []),
  ];

  return (
    <>
      {isTransitioning && (
        <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black bg-opacity-70">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-white text-lg font-semibold">{transitionMessage}</p>
        </div>
      )}
      {isGhostMode && (
        <GhostModeBanner ghostedUser={ghostedUser} onExit={exitGhostMode} />
      )}
      <UpdateAvailableBanner />
      <div
        className={`all bg-canvas-userbar min-h-screen ${open && showSideBar ? "opened-sidebar" : " w-full"
          } ${isGhostMode ? "pt-14" : ""}`}
      >
        <div
          className={`sticky ${isGhostMode ? "top-14" : "top-0"} z-50 py-3 px-5  flex items-center bg-canvas-userbar ${showSideBar ? "justify-between" : "justify-end"
            }`}
        >
          {showSideBar && <Sidebar open={open} setOpen={setOpen} />}
          {showSideBar && (
            <div className="flex md:hidden items-center gap-2">
              <button
                className="text-canvas-primary drawer-content"
                data-hs-overlay="#hs-overlay-left"
              >
                <RiMenu3Fill className="text-[30px] cursor-pointer" />
              </button>
              <VersionLabel version={currentVersion} colorClassName="text-canvas-secondery" />
            </div>
          )}
          <Usersbar
            hideSidebar={toggleSidebar}
            hideSidebarChecked={hideSidebarChecked}
            setHideSidebarChecked={setHideSidebarChecked}
          />
          <div
            id="hs-overlay-left"
            className="hs-overlay hs-overlay-open:translate-x-0  -translate-x-full fixed top-0 start-0 transition-all duration-300 transform h-full bg-black bg-opacity-0  w-full z-[60] hidden border-r border-canvas-white border-opacity-10  [--overlay-backdrop:false] [--body-scroll:true]"
            tabIndex="-1"
          >
            <div className="bg-canvas-primary max-w-[220px] w-full h-full py-5 pr-3 flex flex-col">
              <div className="flex justify-between items-center">
                <div className="px-3">
                  <Link to="/">
                    <img src={logo} alt="logo" />
                  </Link>
                </div>
                <button
                  type="button"
                  className="inline-flex flex-shrink-0 justify-center items-center h-8 w-8 rounded-md text-canvas-sidebar hover:text-canvas-white"
                  data-hs-overlay="#hs-overlay-left"
                >
                  <RxCross2 className="w-7 h-7" />
                </button>
              </div>
              <div className="pt-5 flex-1 overflow-y-auto">
                <div>
                  {SidebarData.map((ele, i) => (
                    <div key={i} className="">
                      <div className="pl-3 pt-5 text-canvas-white font-outfit font-extrabold">
                        {ele.title}
                      </div>
                      <div className="">
                        {ele.data.map((e, i) => (
                          <Link
                            data-hs-overlay="#hs-overlay-left"
                            className={`text-canvas-sidebar text-[15px] font-outfit font-medium py-[10px] pl-3 flex items-center gap-3 pr-3 border-b-2 border-canvas-primary hover:rounded-e-2xl hover:border-b-2 hover:border-canvas-white hover:bg-canvas-white hover:bg-opacity-10 hover:mr-3 transition-all duration-500 ease-in-out ${location.pathname === e.path &&
                              "rounded-e-2xl border-b-2 border-canvas-white bg-canvas-white bg-opacity-10 text-canvas-white hover:mr-3"
                              }`}
                            to={e.path}
                            key={i}
                          >
                            {e.icon}
                            {e.text}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <VersionLabel version={currentVersion} className="px-3 pt-3 flex-shrink-0" />
            </div>
          </div>
        </div>
        <Outlet />
      </div>
      <LockButton
        hideSidebar={toggleSidebar}
        hideSidebarChecked={hideSidebarChecked}
        setHideSidebarChecked={setHideSidebarChecked}
      />
    </>
  );
};

const ToastPortal = () => {
  return ReactDOM.createPortal(
    <Toaster
      // position="top-right" // or any position you prefer
      toastOptions={{
        style: {
          zIndex: "999999 !important", // Ensure this is higher than your portal's z-index
        },
      }}
      containerStyle={{
        zIndex: 99999999,
      }}
    />,
    document.body
  );
};

function App() {
  useFirebaseMessaging(messagingReady);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    // Strip the cache-busting param added during ghost mode transitions
    const url = new URL(window.location.href);
    if (url.searchParams.has('_reload')) {
      url.searchParams.delete('_reload');
      window.history.replaceState(null, '', url.pathname + (url.search || '') + (url.hash || ''));
    }
  }, []);

  return (
    <>
      <div>
        <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<CheckLoginRoute />}>
              <Route path="/" element={<Login />} />
              <Route path="/forget-password" element={<ForgetPWd />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            <Route element={<GhostModeProvider><ProtectedRoute /></GhostModeProvider>}>
              <Route path="/canvass-map" element={<CanvassMapPage />} />
              <Route path="/surveyentries" element={<SurveyEntryPage />} />
              <Route path="/dashboard" element={<DigitalReachPage />} />
              <Route element={<RoleProtectedRoute disallowedRoles={[ROLES.REGIONAL_MANAGER, ROLES.CANVASSER]} />}>
                <Route path="/directory" element={<DirectoryPage />} />
              </Route>
              <Route element={<RoleProtectedRoute disallowedRoles={[ROLES.REGIONAL_MANAGER]} />}>
                <Route path="/import-data" element={<ImportDataPage />} />
              </Route>
              <Route path="/walking-list" element={<WalkingListPage />} />
              <Route path="/documents" element={<DocumentationPage />} />
              <Route path="/accountSetting" element={<AccountSetting />} />
              <Route path="/profileSetting" element={<ProfileSetting />} />
              <Route path="/form" element={<HubsportForm />} />
              <Route path="/schedule-meeting" element={<ScheduleMeet />} />
              <Route element={<RoleProtectedRoute disallowedRoles={[ROLES.CANVASSER]} />}>
                <Route path="/database" element={<DatabasePage />} />
                <Route path="/households" element={<HouseholdsPage />} />
                <Route path="/theme-settings" element={<ThemeSettings />} />
              </Route>
              <Route element={<RoleProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ACCOUNT_MANAGER, ROLES.REGIONAL_MANAGER]} />}>
                <Route path="/tenant-management" element={<TenantManagementPage />} />
              </Route>
              <Route element={<RoleProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ACCOUNT_MANAGER]} />}>
                <Route path="/tenant-management/:id/edit" element={<TenantFormPage />} />
                <Route path="/tenant-management/new" element={<TenantFormPage />} />
              </Route>
            </Route>
            <Route path="/select-tenant" element={<SelectTenantPage />} />
            <Route path="/auth/cross-login" element={<CrossLoginBridge />} />
            <Route path="/voter-servey" element={<VoterForm />} />
            <Route path="/generate-password" element={<GeneratePassword />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ThemeProvider>
      </div>
      <ToastPortal />
      <DiscardOfflineChangesPrompt />
      {/* NotificationPrompt owns its own usePushNotifications hook — no props needed */}
      {isLoggedIn && <NotificationPrompt />}
    </>
  );
}

export default App;
